import { CategoryChartQueryFn, CategoryChartQueryParam } from "../categoryChart";
import {
    deltaValue,
    getBaseValue,
    // getKpiReportFromGQResult, KpiReport, KpiReportEntity,
    roundValue,
    Unit
} from "../processors";
// import { queryEntities } from "../misc";
import _ from "lodash";
import { FigureHorStackBarKpiProvider } from "./categoryChartData.types";
import { getKpisForProvidersWithCategoryChart } from "../opendata";
import { figureDeltaString } from "./figureDeltaString";
import { VALUE_STATUS_NAMING } from "../../Components/misc";

export const fetchFigureHorStackBarKpiProviderChartData: CategoryChartQueryFn = async ({
    categoryChart,
    providers,
    kpis,
    category
}: CategoryChartQueryParam): Promise<FigureHorStackBarKpiProvider> => {
    const provider = providers?.[0];
    if (!provider || !categoryChart || kpis == null || !kpis?.length) return null;

    let _kpiReportResults = await getKpisForProvidersWithCategoryChart(kpis, [provider], false, categoryChart);
    if (!_kpiReportResults) return null;

    const summaryKpis = kpis?.filter(d => d?.[3]?.toString()?.indexOf("useAsSummary") > -1);
    const summaryKpiMap = _.keyBy(summaryKpis, d => d?.[0]);
    const otherLabel = _.uniq(summaryKpis.map(d => d?.[3]).map(d => d?.split("_")?.[1]))?.find(
        d => d != null && d.toString().trim() !== ""
    );

    const elementKpis = kpis?.filter(d => (d?.[3] ?? "")?.indexOf("useAsSummary") < 0);
    const elementKpiMap = _.keyBy(elementKpis, d => d?.[0]);

    const kpiReportResults = (_kpiReportResults ?? []).filter(
        d => elementKpiMap[d.kpiCode] != null && d != null && d.provider != null
    );

    const summaryKpiReportResults = (_kpiReportResults ?? []).filter(
        d => summaryKpiMap[d.kpiCode] != null && d != null && d.provider != null
    );

    // group by year
    const groupByYearKpiReportResults = _.groupBy(kpiReportResults, d => d.year);
    const groupByYearKpiReportSummaryResults = _.groupBy(summaryKpiReportResults, d => d.year);

    const years = _.uniq(_.orderBy(kpiReportResults?.map(d => d.year) ?? [], d => d, "desc"));

    const latestYear = years?.[0];
    const previousYear = years?.[1];

    if (latestYear == null) return null;

    const reports = groupByYearKpiReportResults[latestYear] ?? [];
    const summaryReports = groupByYearKpiReportSummaryResults[latestYear] ?? [];
    const prevReports = groupByYearKpiReportResults[previousYear ?? ""] ?? [];
    const summaryPrevReports = groupByYearKpiReportSummaryResults[previousYear ?? ""] ?? [];
    const units = _.uniq(
        reports?.map(report => report.unit)?.filter(d => d != null && d.toString().trim() !== "") ?? []
    );
    let unit = units[0] as Unit;

    if (unit == null) return null;

    const { figureFooter, figureHeader, visHeader, visFooter, displayTitle, visType } = categoryChart;
    const kpiCodeSchemeTypeToKpiMetrics = _.keyBy(kpis, kpi => {
        return `${kpi[0]} - ${kpi[1]}`;
    });

    const currentYearValues = (summaryReports?.length > 0 ? summaryReports : reports)
        .map(d => getBaseValue(d?.value, d?.unit))
        .filter(d => d.value != null);
    const previousYearValues = (summaryPrevReports?.length > 0 ? summaryPrevReports : prevReports)
        .map(d => getBaseValue(d?.value, d?.unit))
        .filter(d => d.value != null);

    const figureValue = currentYearValues.length > 0 ? _.sumBy(currentYearValues, d => d.value ?? 0) ?? 0 : null;
    const figureValueStatus = reports.find(d => d.value == null)?.valueStatus;

    const prevFigureValue = previousYearValues.length > 0 ? _.sumBy(previousYearValues, d => d.value ?? 0) ?? 0 : null;

    // create chart data
    const _chartData =
        reports
            ?.map(report => {
                const kpiCodeSchemeType = `${report.kpiCode} - ${report.schemeType}`;

                if (!kpiCodeSchemeTypeToKpiMetrics[kpiCodeSchemeType] || report.value == null) return null;

                const baseValue = getBaseValue(report?.value, report.unit);
                if (baseValue?.value != null) unit = baseValue?.unit;

                return {
                    value: baseValue?.value,
                    // unit: baseValue?.unit,
                    label: kpiCodeSchemeTypeToKpiMetrics[kpiCodeSchemeType]?.[2] ?? report.indicatorTitle,
                    seriesName: report.indicatorTitle
                };
            })
            ?.filter(d => d != null) ?? [];

    if (figureValue != null && figureValue > _.sumBy(_chartData, d => d.value ?? 0)) {
        const all = _.sumBy(_chartData, d => d.value ?? 0);
        _chartData.push({
            value: figureValue - all,
            label: otherLabel ?? "Other",
            seriesName: otherLabel ?? "Other"
        });
    }

    const chartData = _.orderBy(_chartData, d => d.value, "desc");

    const initialYear = years[years?.length - 1];
    const visHeaderProcessed = _.template(visHeader?.value ?? "")({
        startYear: initialYear,
        endYear: latestYear
    });

    const visFooterProcessed = _.template(visFooter?.value ?? "")({
        startYear: initialYear,
        endYear: latestYear
    });

    // find delta of values between latest year and previous year
    const delta = deltaValue(prevFigureValue, figureValue, unit);
    const deltaString = figureDeltaString(delta, category);
    // create content for header and footer of the figure
    const figureHeaderProcessed = _.template(figureHeader?.value ?? "")({
        year: latestYear,
        value: figureValue,
        deltaString
    });

    const figureFooterProcessed = _.template(figureFooter?.value ?? "")({
        year: latestYear,
        value: figureValue,
        deltaString
    });

    const ret: FigureHorStackBarKpiProvider = {
        unit: unit,
        year: latestYear,
        waterProvider: provider,
        title: displayTitle,
        footer: null,
        figure: !(visType === "figure" || visType === "figureWithStackHorBarChart")
            ? null
            : {
                  unit: roundValue(figureValue, unit as Unit).unit as Unit,
                  decimals: roundValue(figureValue, unit as Unit).decimals,
                  header:
                      !figureHeader || figureHeader.processed.trim() === ""
                          ? null
                          : {
                                processed: figureHeaderProcessed,
                                value: figureHeaderProcessed,
                                format: figureHeaderProcessed
                            },
                  value:
                      figureValue == null
                          ? figureValueStatus != null
                              ? VALUE_STATUS_NAMING?.[figureValueStatus] ?? figureValueStatus
                              : prevFigureValue == null
                              ? VALUE_STATUS_NAMING.NR
                              : VALUE_STATUS_NAMING.MD
                          : roundValue(figureValue, unit as Unit).value,
                  footer:
                      !figureFooter || figureFooter.processed.trim() === ""
                          ? null
                          : {
                                processed: figureFooterProcessed,
                                value: figureFooterProcessed,
                                format: figureFooterProcessed
                            }
              },
        graph:
            visType === "figure"
                ? null
                : {
                      header:
                          !visHeader || visHeader.processed.trim() === ""
                              ? null
                              : {
                                    processed: visHeaderProcessed,
                                    value: visHeaderProcessed,
                                    format: visHeaderProcessed
                                },
                      footer:
                          !visFooter || visFooter.processed.trim() === ""
                              ? null
                              : {
                                    processed: visFooterProcessed,
                                    value: visFooterProcessed,
                                    format: visFooterProcessed
                                },
                      data: chartData
                  }
    };

    return ret;
};
